import React, { useContext, useState } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import AuthContext from "../../../contexts/AuthContext/AuthContext";
import { apiErrorMessage } from "../../CareTeam/careUtils";
import PasswordInput from "../../common/PasswordInput";
import { afterSignInPath } from "../SignInPage/SignInPage";
import "../Auth.css";

// Signs the new player straight in. The welcome email asks them to confirm
// their address, but nothing waits for it.
const RegisterPage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    re_password: "",
    birth_year: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.re_password) {
      setError("The two passwords don't match.");
      return;
    }
    const year = form.birth_year.trim();
    if (year && !/^\d{4}$/.test(year)) {
      setError("Please enter your birth year, like 1951, or leave it blank.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${SERVER_URL}/api/register/`, {
        ...form,
        birth_year: year ? Number(year) : null,
      });
      login(data.user, data.access, data.refresh);
      navigate(afterSignInPath(data.user));
    } catch (err) {
      setError(apiErrorMessage(err, "We couldn't create your account. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Register – Mend Grove</title>
        <meta name="description" content="" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://mendgrove.com/register" />
        <meta property="og:title" content="Register – Mend Grove" />
        <meta property="og:description" content="" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mendgrove.com/register" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Register – Mend Grove" />
        <meta name="twitter:description" content="" />
        <meta name="twitter:image" content="https://mendgrove.com/logo192.png" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "Mend Grove",
              "url": "https://mendgrove.com/",
              "logo": "https://mendgrove.com/logo192.png",
              "sameAs": [
                "https://www.linkedin.com/company/mendgrove",
                "https://www.facebook.com/mendgrove"
              ],
              "description": "Mend Grove offers gentle, science-based brain-training games to support cognitive recovery and everyday mental exercise.",
              "contactPoint": {
                "@type": "ContactPoint",
                "email": "support@mendgrove.com",
                "contactType": "customer support",
                "url": "https://mendgrove.com/contact"
              }
            }
          `}
        </script>
      </Helmet>
      <div className="auth-card">
        <h2 className="auth-title">Create your account</h2>
        <form onSubmit={handleRegister}>
          <div className="auth-field">
            <label htmlFor="name" className="auth-label">
              Your name <span className="auth-required">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={form.name}
              onChange={set("name")}
              className="auth-input"
              required
              autoFocus
              autoComplete="name"
            />
          </div>
          <div className="auth-field">
            <label htmlFor="email" className="auth-label">
              Email <span className="auth-required">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={form.email}
              onChange={set("email")}
              className="auth-input"
              required
              autoComplete="email"
            />
          </div>
          <div className="auth-field">
            <label htmlFor="password" className="auth-label">
              Password <span className="auth-required">*</span>
            </label>
            <PasswordInput
              id="password"
              value={form.password}
              onChange={set("password")}
              required
              autoComplete="new-password"
            />
          </div>
          <div className="auth-field">
            <label htmlFor="repeatPassword" className="auth-label">
              Repeat password <span className="auth-required">*</span>
            </label>
            <PasswordInput
              id="repeatPassword"
              value={form.re_password}
              onChange={set("re_password")}
              required
              autoComplete="new-password"
            />
          </div>
          <div className="auth-field">
            <label htmlFor="birthYear" className="auth-label">
              Birth year <span className="auth-optional">(optional)</span>
            </label>
            <input
              type="text"
              id="birthYear"
              inputMode="numeric"
              maxLength={4}
              placeholder="e.g. 1951"
              value={form.birth_year}
              onChange={set("birth_year")}
              className="auth-input"
              aria-describedby="birthYearHelp"
              autoComplete="bday-year"
            />
            <p id="birthYearHelp" className="auth-hint">
              Lets us compare your scores with players your age.
            </p>
          </div>
          {error && (
            <p className="auth-message auth-message--error" aria-live="polite">
              {error}
            </p>
          )}
          <button type="submit" className="auth-submit" disabled={busy}>
            {busy ? "Creating account..." : "Create account"}
          </button>
        </form>
        <div className="auth-footer">
          <p>
            Already have an account?{" "}
            <Link to="/signin" className="auth-link">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
